/**
 * Multiplies by a power of ten **through decimal notation**, so the result is
 * the number a human would write rather than the nearest float to it.
 *
 * `0.07 * 100` is `7.000000000000001` and `0.29 * 100` is `28.999999999999996`:
 * binary floating point has no exact 0.07, so the product carries the error the
 * literal already had. Shifting the **exponent** of the decimal string instead
 * never performs the multiplication, so there is nothing to accumulate — and it
 * is one rounding rather than two, which is what makes `roundTo` below correct
 * at the halfway cases.
 *
 * The `toExponential()` round-trip matters: a plain `Number(\`${value}e2\`)` is
 * `NaN` for anything JavaScript already stringifies with an exponent (every
 * magnitude below 1e-6), which is a silent hole in the naive version.
 */
export function scaleByPowerOfTen(value: number, power: number): number {
  if (!Number.isFinite(value) || power === 0) return value;

  const [mantissa, exponent] = value.toExponential().split('e');
  return Number(`${mantissa ?? '0'}e${String(Number(exponent) + power)}`);
}

/**
 * Rounds to a fixed number of decimal places, half away from zero.
 *
 * `Math.round(value * 10 ** decimals) / 10 ** decimals` is the usual version and
 * it is wrong at the boundary: `1.005 * 100` is `100.49999999999999`, so it
 * rounds down to `1` where every operator expects `1.01`. Scaling by exponent
 * first removes the multiplication error, leaving `Math.round` a number that is
 * actually at the halfway point.
 */
export function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;

  const shifted = scaleByPowerOfTen(value, decimals);
  // `Math.round` breaks ties toward +∞, which would round -2.5 to -2 while
  // rounding 2.5 to 3. A percentage either side of zero should behave the same.
  const rounded = Math.sign(shifted) * Math.round(Math.abs(shifted));

  return scaleByPowerOfTen(rounded, -decimals);
}
